'use client';

import { Bell } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { NotificationsPanel } from '@/components/global/NotificationsPanel';
import { TopLevelToggle } from '@/components/global/TopLevelToggle';
import { UserAvatarMenu } from '@/components/global/UserAvatarMenu';
import { Wordmark } from '@/components/brand';
import { useShellRuntime } from '@/components/shell/shell-runtime-provider';
import { t } from '@/lib/design/tokens';

// Routes that should never show the global header (marketing site,
// pre-auth flows, onboarding chrome, error pages). The header is an
// authenticated-app concern — keeping it off of these paths preserves
// the existing marketing / auth surfaces untouched.
const HIDDEN_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/privacy',
  '/terms',
  '/auth',
  '/onboarding',
  '/error',
  '/not-found',
];

// Canon routes (app/(canon)/*) mount their own gh-bar — the legacy
// GlobalHeader must not render on top. These are exact-match prefixes.
const CANON_HIDDEN_PATHS = new Set<string>([
  '/',
  '/sell',
  '/attract',
  '/respond',
  '/publish',
  '/contacts',
  '/composer',
]);

const CANON_PREFIX_HIDDEN = [
  '/sell/',
  '/attract/',
  '/respond/',
  '/publish/',
  '/contacts/',
  '/composer/',
  '/settings/',
  '/settings',
];

function shouldHideHeader(pathname: string): boolean {
  if (HIDDEN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true;
  if (CANON_HIDDEN_PATHS.has(pathname)) return true;
  if (CANON_PREFIX_HIDDEN.some((prefix) => pathname.startsWith(prefix))) return true;
  return false;
}

export function GlobalHeader() {
  const pathname = usePathname() ?? '/';
  const [bellOpen, setBellOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const { notifications, providerMounted, creditsLabel } = useShellRuntime();

  // Without the ShellRuntimeProvider mounted we are not in an
  // authenticated app surface (e.g. marketing site rendered from `/`).
  if (!providerMounted) return null;
  if (shouldHideHeader(pathname)) return null;

  const unreadCount = notifications.length;

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        background: t.bg0,
        borderBottom: `1px solid ${t.border}`,
        fontFamily: t.font,
      }}
    >
      <Link href="/" style={{ textDecoration: 'none', display: 'inline-flex' }}>
        <Wordmark size={16} style={{ color: t.text1 }} />
      </Link>

      <TopLevelToggle />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          position: 'relative',
        }}
      >
        {creditsLabel && (
          <div
            style={{
              background: t.espressoMuted,
              borderRadius: 999,
              padding: '2px 8px',
              fontSize: 10,
              fontWeight: 600,
              color: t.espresso,
              fontFamily: t.font,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {creditsLabel}
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            setBellOpen((v) => !v);
            setAvatarOpen(false);
          }}
          aria-label="Notifications"
          aria-expanded={bellOpen}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
            color: t.text2,
            padding: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Bell size={18} strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: 999,
                background: t.espresso,
              }}
            />
          )}
        </button>
        {bellOpen && (
          <NotificationsPanel onClose={() => setBellOpen(false)} />
        )}

        <UserAvatarMenu
          open={avatarOpen}
          onOpenChange={(next) => {
            setAvatarOpen(next);
            if (next) setBellOpen(false);
          }}
        />
      </div>
    </header>
  );
}
